/**
 * Ray Bundle offer
 *
 * "Main product + 1-2 add-on products" widget (blocks/ray-bundle-offer.liquid). Lets the
 * shopper pick each add-on's size inline and add the main product + checked add-ons to the
 * cart in a single request.
 *
 * Cart integration deliberately reuses the theme's native contract instead of a bespoke one:
 * `ThemeEvents.cartUpdate` ('cart:update') is what @theme/events' CartAddEvent dispatches, and
 * what the cart drawer / cart bubble / quick-add dialog already listen for (see
 * assets/product-form.js, assets/quick-add.js). This file can't `import` that class (it loads
 * as a plain script, not a module - see snippets/ray-cart-cross-sell.js for the same
 * constraint), so it dispatches a plain CustomEvent with the same type and detail shape instead
 * of hand-rolling its own event names/refresh logic like assets/ray-cart-cross-sell.js or
 * assets/ray-mix-match-builder.js do.
 */
(() => {
  if (window.RayBundleOfferInitialized) return;

  window.RayBundleOfferInitialized = true;

  const CART_UPDATE_EVENT = 'cart:update';

  const selectors = {
    root: '[data-ray-bundle-offer]',
    item: '[data-ray-bundle-item]',
    main: '[data-ray-bundle-main]',
    variantsJson: '[data-ray-bundle-item-variants]',
    variantIdInput: '[data-ray-bundle-variant-id]',
    checkbox: '[data-ray-bundle-checkbox]',
    optionControl: '[data-ray-bundle-option]',
    sizeSelect: '[data-ray-bundle-size-select]',
    price: '[data-ray-bundle-item-price]',
    comparePrice: '[data-ray-bundle-item-compare-price]',
    totals: '[data-ray-bundle-totals]',
    total: '[data-ray-bundle-total]',
    totalOriginal: '[data-ray-bundle-total-original]',
    savings: '[data-ray-bundle-savings]',
    savingsAmount: '[data-ray-bundle-savings-amount]',
    addButton: '[data-ray-bundle-add-button]',
    error: '[data-ray-bundle-error]',
  };

  let instanceCounter = 0;

  const getVariants = (item) => {
    const json = item.querySelector(selectors.variantsJson);

    if (!json) return [];

    try {
      return JSON.parse(json.textContent.trim());
    } catch (error) {
      console.error('Ray bundle offer variants JSON parse failed', error);
      return [];
    }
  };

  const applyMoneyFormat = (cents, format) => {
    const amount = (cents / 100).toFixed(2);

    if (!format) return amount;

    const noDecimal = Math.round(cents / 100).toString();
    const withComma = amount.replace('.', ',');

    return format
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', noDecimal)
      .replace('{{amount_with_comma_separator}}', withComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', noDecimal);
  };

  const getMoneyFormatter = (root) => {
    const format = root.dataset.moneyFormat;
    return (amountInCents) => applyMoneyFormat(Number(amountInCents) || 0, format);
  };

  const getSelectedOptions = (item) => {
    const selectedOptions = {};

    item.querySelectorAll(selectors.optionControl).forEach((control) => {
      const position = control.dataset.optionPosition;

      if (!position) return;
      if (control.type === 'radio' && !control.checked) return;

      selectedOptions[`option${position}`] = control.value;
    });

    return selectedOptions;
  };

  const variantMatchesOptions = (variant, selectedOptions) => {
    return Object.entries(selectedOptions).every(([key, value]) => !value || variant[key] === value);
  };

  const findVariant = (item) => {
    const variants = getVariants(item);
    const selectedOptions = getSelectedOptions(item);

    if (Object.keys(selectedOptions).length === 0) {
      return variants.find((variant) => variant.available) || variants[0];
    }

    return (
      variants.find((variant) => variant.available && variantMatchesOptions(variant, selectedOptions)) ||
      variants.find((variant) => variantMatchesOptions(variant, selectedOptions)) ||
      variants.find((variant) => variant.available) ||
      variants[0]
    );
  };

  const updateSizeAvailability = (item) => {
    const select = item.querySelector(selectors.sizeSelect);

    if (!select) return;

    const variants = getVariants(item);
    const selectedOptions = getSelectedOptions(item);
    const position = select.dataset.optionPosition;
    const key = `option${position}`;
    const optionsWithoutSize = { ...selectedOptions };

    delete optionsWithoutSize[key];

    Array.from(select.options).forEach((option) => {
      const testOptions = { ...optionsWithoutSize, [key]: option.value };
      const hasAvailableVariant = variants.some((variant) => variant.available && variantMatchesOptions(variant, testOptions));

      option.disabled = !hasAvailableVariant;
    });

    if (select.selectedOptions[0]?.disabled) {
      const firstAvailable = Array.from(select.options).find((option) => !option.disabled);

      if (firstAvailable) select.value = firstAvailable.value;
    }
  };

  const updateItem = (item, formatMoney) => {
    updateSizeAvailability(item);

    const variant = findVariant(item);
    const variantIdInput = item.querySelector(selectors.variantIdInput);
    const priceEl = item.querySelector(selectors.price);
    const comparePriceEl = item.querySelector(selectors.comparePrice);
    const checkbox = item.querySelector(selectors.checkbox);
    const unavailableLabel = item.closest(selectors.root)?.dataset.unavailableLabel || '';

    if (!variant) return;

    if (variantIdInput) variantIdInput.value = variant.id;

    if (priceEl) {
      priceEl.textContent = variant.available ? formatMoney(variant.price) : unavailableLabel;
    }

    if (comparePriceEl) {
      if (variant.available && variant.compareAtPrice && variant.compareAtPrice > variant.price) {
        comparePriceEl.textContent = formatMoney(variant.compareAtPrice);
        comparePriceEl.hidden = false;
      } else {
        comparePriceEl.textContent = '';
        comparePriceEl.hidden = true;
      }
    }

    if (checkbox && !variant.available) {
      checkbox.checked = false;
      checkbox.disabled = true;
    }
  };

  const isPackPickerInPackMode = () => document.getElementById('ray-pack-picker')?.dataset.packMode === 'true';

  const getDiscountPercent = (root, checkedAddonCount) => {
    if (checkedAddonCount === 0) return 0;

    const discountOnePercent = Number(root.dataset.discountOnePercent) || 0;
    const discountBothPercent = Number(root.dataset.discountBothPercent) || 0;
    const tierPercent = checkedAddonCount === 1 ? discountOnePercent : checkedAddonCount === 2 ? discountBothPercent : 0;

    if (isPackPickerInPackMode()) {
      const packPercent = Number(root.dataset.discountPackPercent) || 0;

      if (packPercent > 0) return packPercent;
    }

    return tierPercent;
  };

  const getPackPickerSelectedOptions = () => {
    const picker = document.getElementById('ray-pack-picker');

    if (!picker) return null;

    const color = picker.querySelector('.rpp-swatch.is-active')?.dataset.color;
    const size = picker.querySelector('.rpp-size-btn.is-active')?.dataset.size;
    const format = picker.querySelector('.rpp-format-tab.is-active')?.dataset.format;
    const selectedOptions = {};

    if (color) selectedOptions.option1 = color;
    if (size) selectedOptions.option2 = size;
    if (format) selectedOptions.option3 = format;

    return selectedOptions;
  };

  const HIGHLIGHT_CLASS = 'rpp-highlight';

  const getPackPickerMeta = () => {
    const metaEl = document.getElementById('ray-pack-meta');

    if (!metaEl) return null;

    try {
      return JSON.parse(metaEl.textContent.trim());
    } catch (error) {
      return null;
    }
  };

  const getPackPickerIncompleteState = () => {
    const picker = document.getElementById('ray-pack-picker');

    if (!picker) return null;

    const atcBtn = picker.querySelector('#rpp-atc-btn');
    const atcText = picker.querySelector('#rpp-atc-text');

    if (!atcBtn || !atcBtn.disabled) return null;

    const labels = getPackPickerMeta()?.labels || {};
    const currentLabel = atcText ? atcText.textContent.trim() : '';

    let section = picker;

    if (labels.chooseSize && currentLabel === labels.chooseSize) {
      section = picker.querySelector('#rpp-size-section') || section;
    } else if (labels.choosePackButton && currentLabel === labels.choosePackButton) {
      section = picker.querySelector('#rpp-pack-cards') || section;
    } else if (labels.chooseAllColors && currentLabel === labels.chooseAllColors) {
      // Target the .rpp-section wrapper itself (not #rpp-custom-slots, its role="list"
      // child) - that's where the message/highlight belongs.
      section = picker.querySelector('#rpp-custom-bundle') || section;
    }

    return { section, message: currentLabel };
  };

  const highlightPackPickerSection = (section) => {
    if (!section) return;

    section.classList.remove(HIGHLIGHT_CLASS);
    void section.offsetWidth; // restart the animation if it's already mid-run
    section.classList.add(HIGHLIGHT_CLASS);
    section.scrollIntoView({ behavior: 'smooth', block: 'center' });

    window.setTimeout(() => section.classList.remove(HIGHLIGHT_CLASS), 1600);
  };

  const SECTION_ERROR_ATTR = 'data-rpp-section-error';

  // Per-section "you still need to pick this" message, appended under the relevant
  // .rpp-section itself (client wants it there, not in the bundle block's own error
  // paragraph - see showError/[data-ray-bundle-error] for actual cart-add failures).
  const getOrCreateSectionError = (section) => {
    let el = section.querySelector(`[${SECTION_ERROR_ATTR}]`);

    if (!el) {
      el = document.createElement('p');
      el.className = 'rpp-section-error';
      el.setAttribute(SECTION_ERROR_ATTR, '');
      el.hidden = true;
      section.appendChild(el);
    }

    return el;
  };

  const clearPackPickerSectionErrors = () => {
    document.querySelectorAll(`[${SECTION_ERROR_ATTR}]`).forEach((el) => {
      el.textContent = '';
      el.hidden = true;
    });
  };

  const showPackPickerSectionError = (incomplete) => {
    clearPackPickerSectionErrors();

    if (!incomplete) return;

    const el = getOrCreateSectionError(incomplete.section);

    el.textContent = incomplete.message;
    el.hidden = false;
  };

	const syncMainItem = (root, productDetails, formatMoney) => {
		const mainItem = root.querySelector(selectors.main);

		if (!mainItem) return;

		const variants = getVariants(mainItem);
		const packPickerEl = document.getElementById('ray-pack-picker');

		let variant;
		
		if (packPickerEl && packPickerEl.dataset.resolvedVariantId) {
			variant = variants.find((v) => String(v.id) === packPickerEl.dataset.resolvedVariantId);
		}

		if (!variant) {
			const variantIdInput = productDetails
				? productDetails.querySelector('form[action*="/cart/add"] input[name="id"]')
				: document.querySelector('form[action*="/cart/add"] input[name="id"]');

			const currentVariantId = variantIdInput ? variantIdInput.value : null;
			variant = variants.find((v) => String(v.id) === String(currentVariantId)) || variants[0];
		}

		const hiddenInput = mainItem.querySelector(selectors.variantIdInput);
		const priceEl = mainItem.querySelector(selectors.price);
		const comparePriceEl = mainItem.querySelector(selectors.comparePrice);

		if (!variant) return;

		if (hiddenInput) hiddenInput.value = variant.id;

		if (priceEl) priceEl.textContent = formatMoney(variant.price);

		if (comparePriceEl) {
			if (variant.compareAtPrice && variant.compareAtPrice > variant.price) {
				comparePriceEl.textContent = formatMoney(variant.compareAtPrice);
				comparePriceEl.hidden = false;
			} else {
				comparePriceEl.textContent = '';
				comparePriceEl.hidden = true;
			}
		}
	};

  const isItemIncluded = (item) => {
    if (item.matches(selectors.main)) return true;

    const checkbox = item.querySelector(selectors.checkbox);

    return !!checkbox && checkbox.checked && !checkbox.disabled;
  };

  const getItemVariant = (item) => {
    const variants = getVariants(item);
    const variantIdInput = item.querySelector(selectors.variantIdInput);

    if (!variantIdInput) return null;

    return variants.find((variant) => String(variant.id) === String(variantIdInput.value)) || null;
  };

  const recomputeTotals = (root, formatMoney) => {
    const items = Array.from(root.querySelectorAll(selectors.item));
    let originalTotal = 0;
    let checkedAddonCount = 0;

    items.forEach((item) => {
      if (!isItemIncluded(item)) return;

      const variant = getItemVariant(item);

      if (!variant || !variant.available) return;

      originalTotal += Number(variant.price) || 0;

      if (!item.matches(selectors.main)) checkedAddonCount += 1;
    });

    const discountPercent = getDiscountPercent(root, checkedAddonCount);

    const savings = discountPercent > 0 ? (originalTotal * discountPercent) / 100 : 0;
    const finalTotal = originalTotal - savings;

    const totalsEl = root.querySelector(selectors.totals);
    const totalEl = root.querySelector(selectors.total);
    const totalOriginalEl = root.querySelector(selectors.totalOriginal);
    const savingsEl = root.querySelector(selectors.savings);
    const savingsAmountEl = root.querySelector(selectors.savingsAmount);

    if (totalsEl) totalsEl.hidden = checkedAddonCount === 0;

    if (totalEl) totalEl.textContent = formatMoney(finalTotal);

    if (totalOriginalEl) {
      if (savings > 0) {
        totalOriginalEl.textContent = formatMoney(originalTotal);
        totalOriginalEl.hidden = false;
      } else {
        totalOriginalEl.hidden = true;
      }
    }

    if (savingsEl) {
      if (savings > 0) {
        savingsEl.hidden = false;

        if (savingsAmountEl) {
          savingsAmountEl.textContent = `${formatMoney(savings)} (-${discountPercent}%)`;
        }
      } else {
        savingsEl.hidden = true;
      }
    }

    const addButton = root.querySelector(selectors.addButton);

    if (addButton) {
      const mainVariant = getItemVariant(root.querySelector(selectors.main));

      addButton.disabled = !mainVariant || !mainVariant.available || checkedAddonCount === 0;
    }
  };

  const showError = (root, message) => {
    const errorEl = root.querySelector(selectors.error);

    if (!errorEl) return;

    errorEl.textContent = message;
    errorEl.hidden = !message;
  };

  const addBundleToCart = async (root) => {
    const addButton = root.querySelector(selectors.addButton);
    const propertyKey = root.dataset.discountPropertyKey || '';

    const includedItems = Array.from(root.querySelectorAll(selectors.item)).filter(isItemIncluded);
    const checkedAddonCount = includedItems.filter((item) => !item.matches(selectors.main)).length;
    const discountPercent = getDiscountPercent(root, checkedAddonCount);

    const properties =
      propertyKey && discountPercent > 0
        ? { [propertyKey]: String(discountPercent), _ray_bundle_discount_key: propertyKey }
        : null;

    const items = includedItems
      .map((item) => getItemVariant(item))
      .filter((variant) => variant && variant.available)
      .map((variant) => ({
        id: Number(variant.id),
        quantity: 1,
        ...(properties ? { properties } : {}),
      }))
      .reverse();

    if (items.length === 0) return;

    showError(root, '');

    const originalText = addButton ? addButton.textContent : '';

    if (addButton) {
      addButton.disabled = true;
      addButton.dataset.loading = 'true';
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];

    cartItemsComponents.forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      }).then((res) => res.json());

      if (response.status) {
        showError(root, response.message || response.description || 'Cart error');

        if (addButton) {
          addButton.disabled = false;
          delete addButton.dataset.loading;
          addButton.textContent = originalText;
        }

        return;
      }

      const cart = await fetch('/cart.js').then((res) => res.json());
      const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

      document.dispatchEvent(
        new CustomEvent(CART_UPDATE_EVENT, {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: root.id || 'ray-bundle-offer',
            data: {
              source: 'ray-bundle-offer',
              itemCount: totalQuantity,
              sections: response.sections,
            },
          },
        })
      );
    } catch (error) {
      console.error('Ray bundle offer add-to-cart failed', error);
      showError(root, 'Cart error');
    } finally {
      if (addButton && addButton.isConnected) {
        addButton.disabled = false;
        delete addButton.dataset.loading;
        addButton.textContent = originalText;
      }
    }
  };

  const initRoot = (root) => {
    if (root.dataset.rayBundleOfferInitialized === 'true') return;

    root.dataset.rayBundleOfferInitialized = 'true';

    if (!root.id) root.id = `ray-bundle-offer-${++instanceCounter}`;

    const productDetails = root.closest('.product-details');
    const formatMoney = getMoneyFormatter(root);
    const items = Array.from(root.querySelectorAll(selectors.item));

    const refresh = () => {
      syncMainItem(root, productDetails, formatMoney);
      items.forEach((item) => {
        if (!item.matches(selectors.main)) updateItem(item, formatMoney);
      });
      recomputeTotals(root, formatMoney);
    };

    root.addEventListener('change', (event) => {
      if (event.target.closest(selectors.checkbox) || event.target.closest(selectors.optionControl)) {
        const item = event.target.closest(selectors.item);
        if (item && !item.matches(selectors.main)) updateItem(item, formatMoney);
        recomputeTotals(root, formatMoney);
      }
    });

    let showingIncompleteError = false;

    root.addEventListener('click', (event) => {
      if (event.target.closest(selectors.addButton)) {
        event.preventDefault();

        const incomplete = getPackPickerIncompleteState();

        if (incomplete) {
          showingIncompleteError = true;
          showPackPickerSectionError(incomplete);
          highlightPackPickerSection(incomplete.section);
          return;
        }

        addBundleToCart(root);
      }
    });

    root.querySelectorAll('.ray-bundle-offer__title').forEach((link) => {
      link.addEventListener('click', (event) => event.stopPropagation());
    });

    document.addEventListener('change', (event) => {
      if (productDetails && !productDetails.contains(event.target)) return;
      if (root.contains(event.target)) return; // already handled by the listener above

      refresh();
    });

    document.addEventListener('variant:update', () => {
      refresh();
    });

    document.addEventListener('click', (event) => {
      const picker = document.getElementById('ray-pack-picker');

      if (!picker || !picker.contains(event.target)) return;

      if (showingIncompleteError) {
        const incomplete = getPackPickerIncompleteState();

        showPackPickerSectionError(incomplete);
        showingIncompleteError = !!incomplete;
      }

      refresh();
    });

    refresh();
  };

  const initAll = (rootNode = document) => {
    rootNode.querySelectorAll(selectors.root).forEach(initRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', () => initAll());
})();
